import React, { useState, useEffect, useContext } from "react";
import { TranslatorContext } from "../../../context/Translator";
import axios from "axios";
import { ButtonComponent } from "../../../components/elements";
import { Modal, Row, Col, Form, Table, Pagination } from "react-bootstrap";
import PageLayout from "../../../layouts/PageLayout";
import { Link } from "react-router-dom";
import Swal from 'sweetalert2';
import { useNavigate } from "react-router-dom";
import { FiPlus, FiEdit, FiEye, FiUser, FiSearch, FiX } from "react-icons/fi";

export default function VendorsPage() {
    const { t } = useContext(TranslatorContext);

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [alertModal, setAlertModal] = useState(false);
    const [currentId, setCurrentId] = useState(null);
    const [currentVendorName, setCurrentVendorName] = useState('');
    const navigate = useNavigate();

    // Pagination and Filter States
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [total, setTotal] = useState(0);
    const [lastPage, setLastPage] = useState(1);
    const [filters, setFilters] = useState({
        name: '',
        email: '',
        contact: '',
        address: '',
        company_name: ''
    });
    const [appliedFilters, setAppliedFilters] = useState({
        name: '',
        email: '',
        contact: '',
        address: '',
        company_name: ''
    });

    const permissions = JSON.parse(localStorage.getItem("permissions")) || [];
    const role = localStorage.getItem("role");

    const hasPermission = (perm) => {
        return permissions.includes(perm);
    };

    // Store Modal States
    const [storeModal, setStoreModal] = useState(false);
    const [storeForm, setStoreForm] = useState({
        name: '',
        email: '',
        contact: '',
        address: '',
        company_name: '',
        img: null
    });
    const [storeErrors, setStoreErrors] = useState({});
    const [storeLoading, setStoreLoading] = useState(false);
    const [storeImagePreview, setStoreImagePreview] = useState(null);

    // Update Modal States
    const [updateModal, setUpdateModal] = useState(false);
    const [updateForm, setUpdateForm] = useState({
        id: null,
        name: '',
        email: '',
        contact: '',
        address: '',
        company_name: '',
        img: null,
        existing_img: ''
    });
    const [updateErrors, setUpdateErrors] = useState({});
    const [updateLoading, setUpdateLoading] = useState(false);
    const [updateImagePreview, setUpdateImagePreview] = useState(null);

    // Refresh trigger state
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    useEffect(() => {
        fetchVendors();
    }, [refreshTrigger, currentPage, perPage, appliedFilters]);

    const fetchVendors = () => {
        setLoading(true);

        const token = localStorage.getItem("authToken");

        // Build query parameters
        const params = new URLSearchParams();
        params.append('page', currentPage);
        params.append('per_page', perPage);

        if (appliedFilters.name) params.append('name', appliedFilters.name);
        if (appliedFilters.email) params.append('email', appliedFilters.email);
        if (appliedFilters.contact) params.append('contact', appliedFilters.contact);
        if (appliedFilters.address) params.append('address', appliedFilters.address);
        if (appliedFilters.company_name) params.append('company_name', appliedFilters.company_name);

        axios.get(`${process.env.REACT_APP_API_URL}/vendors?${params.toString()}`, {
            headers: {
                Authorization: `Bearer ${token}`
            }
        })
            .then(res => {
                // Handle both paginated and non-paginated responses
                if (res.data.data) {
                    // Paginated response
                    setData(res.data.data);
                    setCurrentPage(res.data.current_page);
                    setTotal(res.data.total);
                    setLastPage(res.data.last_page);
                } else if (Array.isArray(res.data.vendors)) {
                    // Non-paginated response (fallback)
                    setData(res.data.vendors);
                    setTotal(res.data.vendors.length);
                    setLastPage(1);
                } else if (Array.isArray(res.data)) {
                    setData(res.data);
                    setTotal(res.data.length);
                    setLastPage(1);
                } else {
                    setData([]);
                    setTotal(0);
                    setLastPage(1);
                }
            })
            .catch(err => {
                console.error(err);
                setData([]);
                setTotal(0);
                setLastPage(1);
            })
            .finally(() => setLoading(false));
    };

    // Filter handlers
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const applyFilters = () => {
        setAppliedFilters({ ...filters });
        setCurrentPage(1); // Reset to first page when applying filters
    };

    const clearFilters = () => {
        const emptyFilters = { name: '', email: '', contact: '', address: '', company_name: '' };
        setFilters(emptyFilters);
        setAppliedFilters(emptyFilters);
        setCurrentPage(1);
    };

    const handlePerPageChange = (e) => {
        setPerPage(parseInt(e.target.value));
        setCurrentPage(1); // Reset to first page when changing items per page
    };

    // Pagination handlers
    const handlePageChange = (page) => {
        if (page >= 1 && page <= lastPage) {
            setCurrentPage(page);
        }
    };

    const renderPagination = () => {
        let items = [];
        const maxVisible = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let endPage = Math.min(lastPage, startPage + maxVisible - 1);

        if (endPage - startPage + 1 < maxVisible) {
            startPage = Math.max(1, endPage - maxVisible + 1);
        }

        // Previous button
        items.push(
            <Pagination.Prev
                key="prev"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
            />
        );

        // First page
        if (startPage > 1) {
            items.push(
                <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
                    1
                </Pagination.Item>
            );
            if (startPage > 2) {
                items.push(<Pagination.Ellipsis key="ellipsis1" disabled />);
            }
        }

        // Page numbers
        for (let page = startPage; page <= endPage; page++) {
            items.push(
                <Pagination.Item
                    key={page}
                    active={page === currentPage}
                    onClick={() => handlePageChange(page)}
                >
                    {page}
                </Pagination.Item>
            );
        }

        // Last page
        if (endPage < lastPage) {
            if (endPage < lastPage - 1) {
                items.push(<Pagination.Ellipsis key="ellipsis2" disabled />);
            }
            items.push(
                <Pagination.Item key={lastPage} onClick={() => handlePageChange(lastPage)}>
                    {lastPage}
                </Pagination.Item>
            );
        }

        // Next button
        items.push(
            <Pagination.Next
                key="next"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === lastPage}
            />
        );

        return items;
    };

    // Function to trigger data refresh
    const refreshData = () => {
        setRefreshTrigger(prev => prev + 1);
    };

    // Store Modal Functions
    const openStoreModal = () => {
        setStoreForm({ name: '', email: '', contact: '', address: '', company_name: '', img: null });
        setStoreErrors({});
        setStoreImagePreview(null);
        setStoreModal(true);
    };

    const handleStoreChange = (e) => {
        const { name, value } = e.target;
        setStoreForm(prev => ({ ...prev, [name]: value }));
        if (storeErrors[name]) {
            setStoreErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleStoreFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setStoreForm(prev => ({ ...prev, img: file }));
            const previewUrl = URL.createObjectURL(file);
            setStoreImagePreview(previewUrl);
        }
        if (storeErrors.img) {
            setStoreErrors(prev => ({ ...prev, img: '' }));
        }
    };

    const handleStoreSubmit = async (e) => {
        e.preventDefault();
        setStoreLoading(true);
        setStoreErrors({});

        const formData = new FormData();
        if (storeForm.name) formData.append('name', storeForm.name);
        if (storeForm.email) formData.append('email', storeForm.email);
        if (storeForm.contact) formData.append('contact', storeForm.contact);
        if (storeForm.address) formData.append('address', storeForm.address);
        if (storeForm.company_name) formData.append('company_name', storeForm.company_name);
        if (storeForm.img) formData.append('img', storeForm.img);

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/vendors`,
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );

            if (response.data.status) {
                setStoreModal(false);
                setStoreForm({ name: '', email: '', contact: '', address: '', company_name: '', img: null });
                setStoreImagePreview(null);
                setStoreErrors({});
                refreshData();

                Swal.fire({
                    icon: 'success',
                    title: t('success'),
                    text: t('Vendor created successfully'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            } else {
                console.error('Vendor creation failed:', response.data);
                if (response.data.errors) {
                    setStoreErrors(response.data.errors);
                } else {
                    setStoreErrors({ general: [response.data.message || 'Failed to create vendor'] });
                }

                Swal.fire({
                    icon: 'error',
                    title: t('error'),
                    text: response.data.message || t('Failed to create vendor'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            }
        } catch (error) {
            console.error('Error creating vendor:', error);
            if (error.response?.status === 422) {
                setStoreErrors(error.response.data.errors || {});
                Swal.fire({
                    icon: 'error',
                    title: t('validation_error'),
                    text: t('Please check the form for errors'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            } else if (error.response?.data?.message) {
                setStoreErrors({ general: [error.response.data.message] });
                Swal.fire({
                    icon: 'error',
                    title: t('error'),
                    text: error.response.data.message,
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            } else {
                setStoreErrors({ general: ['An error occurred while creating the vendor'] });
                Swal.fire({
                    icon: 'error',
                    title: t('error'),
                    text: t('An error occurred while creating the vendor'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            }
        } finally {
            setStoreLoading(false);
        }
    };

    // Update Modal Functions
    const openUpdateModal = (item) => {
        setUpdateForm({
            id: item.id,
            name: item.name || '',
            email: item.email || '',
            contact: item.contact || '',
            address: item.address || '',
            company_name: item.company_name || '',
            img: null,
            existing_img: item.img || ''
        });
        setUpdateErrors({});
        setUpdateImagePreview(item.img ? `${process.env.REACT_APP_IMG_URL}/${item.img}` : null);
        setUpdateModal(true);
    };

    const handleUpdateChange = (e) => {
        const { name, value } = e.target;
        setUpdateForm(prev => ({ ...prev, [name]: value }));
        if (updateErrors[name]) {
            setUpdateErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleUpdateFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setUpdateForm(prev => ({ ...prev, img: file }));
            const previewUrl = URL.createObjectURL(file);
            setUpdateImagePreview(previewUrl);
        }
        if (updateErrors.img) {
            setUpdateErrors(prev => ({ ...prev, img: '' }));
        }
    };

    const handleUpdateSubmit = async (e) => {
        e.preventDefault();
        setUpdateLoading(true);
        setUpdateErrors({});

        const formData = new FormData();
        if (updateForm.name) formData.append('name', updateForm.name);
        if (updateForm.email) formData.append('email', updateForm.email);
        if (updateForm.contact) formData.append('contact', updateForm.contact);
        if (updateForm.address) formData.append('address', updateForm.address);
        if (updateForm.company_name) formData.append('company_name', updateForm.company_name);
        if (updateForm.img) formData.append('img', updateForm.img);
        formData.append('_method', 'PUT');

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/vendors/${updateForm.id}`,
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );

            if (response.data.data || response.data.status) {
                setUpdateModal(false);
                setUpdateForm({
                    id: null,
                    name: '',
                    email: '',
                    contact: '',
                    address: '',
                    company_name: '',
                    img: null,
                    existing_img: ''
                });
                setUpdateImagePreview(null);
                setUpdateErrors({});
                refreshData();

                Swal.fire({
                    icon: 'success',
                    title: t('success'),
                    text: t('Vendor updated successfully'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            } else {
                console.error('Vendor update failed:', response.data);
                if (response.data.errors) {
                    setUpdateErrors(response.data.errors);
                } else {
                    setUpdateErrors({ general: [response.data.message || 'Failed to update vendor'] });
                }

                Swal.fire({
                    icon: 'error',
                    title: t('error'),
                    text: response.data.message || t('Failed to update vendor'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            }
        } catch (error) {
            console.error('Error updating vendor:', error);
            if (error.response?.status === 422) {
                setUpdateErrors(error.response.data.errors || {});
                Swal.fire({
                    icon: 'error',
                    title: t('validation_error'),
                    text: t('Please check the form for errors'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            } else if (error.response?.data?.message) {
                setUpdateErrors({ general: [error.response.data.message] });
                Swal.fire({
                    icon: 'error',
                    title: t('error'),
                    text: error.response.data.message,
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            } else {
                setUpdateErrors({ general: ['An error occurred while updating the vendor'] });
                Swal.fire({
                    icon: 'error',
                    title: t('error'),
                    text: t('An error occurred while updating the vendor'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            }
        } finally {
            setUpdateLoading(false);
        }
    };

    // Delete confirmation with SweetAlert
    const confirmDelete = (id, name) => {
        Swal.fire({
            title: t('Are you sure?'),
            text: t(`Want to delete vendor "${name}"?`),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: t('delete'),
            cancelButtonText: t('cancel')
        }).then((result) => {
            if (result.isConfirmed) {
                handleDelete(id, name);
            }
        });
    };

    const handleDelete = async (id, name) => {
        try {
            const response = await axios.delete(`${process.env.REACT_APP_API_URL}/vendors-delete/${id}`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("authToken")}`
                }
            });

            if (response.data.success || response.status === 200) {
                setAlertModal(false);
                setCurrentId(null);
                setCurrentVendorName('');
                refreshData();

                Swal.fire({
                    icon: 'success',
                    title: t('deleted'),
                    text: t(`Vendor "${name}" deleted successfully`),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            } else {
                console.error('Delete failed:', response.data);
                Swal.fire({
                    icon: 'error',
                    title: t('error'),
                    text: response.data.message || t('Failed to delete vendor'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            }
        } catch (error) {
            console.error('Error deleting vendor:', error);
            Swal.fire({
                icon: 'error',
                title: t('error'),
                text: error.response?.data?.message || t('Failed to delete vendor. Please try again.'),
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true
            });
        }
    };

    const handleStoreModalClose = () => {
        setStoreModal(false);
        setTimeout(() => {
            setStoreForm({ name: '', email: '', contact: '', address: '', company_name: '', img: null });
            setStoreErrors({});
            setStoreImagePreview(null);
            setStoreLoading(false);
        }, 100);
    };

    const handleUpdateModalClose = () => {
        setUpdateModal(false);
        setTimeout(() => {
            setUpdateForm({
                id: null,
                name: '',
                email: '',
                contact: '',
                address: '',
                company_name: '',
                img: null,
                existing_img: ''
            });
            setUpdateErrors({});
            setUpdateImagePreview(null);
            setUpdateLoading(false);
        }, 100);
    };

    if (loading && data.length === 0) return <PageLayout><div className="text-center p-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">{t("loading")}...</span></div><p className="mt-3">{t("loading")}...</p></div></PageLayout>;

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card">
                        <div className="mc-breadcrumb p-3">
                            <h3 className="mc-breadcrumb-title mb-0" >{t('vendors')}</h3>
                            <ul className="mc-breadcrumb-list mb-0" >
                                <li className="mc-breadcrumb-item">
                                    <Link to="#" className="mc-breadcrumb-link" style={{color:'#C9A227'}}>{t('home')}</Link>
                                </li>
                                <li className="mc-breadcrumb-item">
                                    <Link to="#" className="mc-breadcrumb-link" style={{color:'#C9A227'}}>{t('vendors')}</Link>
                                </li>
                                <li className="mc-breadcrumb-item" >{t('list')}</li>
                            </ul>
                        </div>
                    </div>
                </Col>

                <Col xl={12}>
                    <div className="mc-card">
                        {/* Add New Button */}
                        <div className="d-flex justify-content-between align-items-center p-3 border-bottom flex-wrap gap-2">
                            <h5 className="mb-0">{t('vendors list')}</h5>
                            {hasPermission("vendor.create") && (
                                <ButtonComponent
                                    type="button"
                                    className="btn add-btn btn-primary d-inline-flex align-items-center gap-2"
                                    onClick={openStoreModal}
                                >
                                    <FiPlus size={16} />
                                    {t('add Vendor')}
                                </ButtonComponent>
                            )}
                        </div>

                        {/* Filter Section */}
                        <div className="p-3 border-bottom">
                            <Row className="g-3 align-items-end">
                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label className="fw-bold small">{t('name')}</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="name"
                                            value={filters.name}
                                            onChange={handleFilterChange}
                                            placeholder={t('search by name')}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={2}>
                                    <Form.Group>
                                        <Form.Label className="fw-bold small">{t('company name')}</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="company_name"
                                            value={filters.company_name}
                                            onChange={handleFilterChange}
                                            placeholder={t('search by company name')}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={2}>
                                    <Form.Group>
                                        <Form.Label className="fw-bold small">{t('email')}</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="email"
                                            value={filters.email}
                                            onChange={handleFilterChange}
                                            placeholder={t('search by email')}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={2}>
                                    <Form.Group>
                                        <Form.Label className="fw-bold small">{t('contact')}</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="contact"
                                            value={filters.contact}
                                            onChange={handleFilterChange}
                                            placeholder={t('search by contact')}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={2}>
                                    <div className="d-flex gap-2">
                                        <ButtonComponent
                                            type="button"
                                            className="btn search-btn btn-primary d-inline-flex align-items-center gap-2"
                                            onClick={applyFilters}
                                        >
                                            <FiSearch size={14} />
                                            {t('search')}
                                        </ButtonComponent>
                                        <ButtonComponent
                                            type="button"
                                            className="btn btn-secondary d-inline-flex align-items-center gap-2"
                                            onClick={clearFilters}
                                        >
                                            <FiX size={14} />
                                            {t('clear')}
                                        </ButtonComponent>
                                    </div>
                                </Col>
                            </Row>

                        </div>

                        {/* Per Page Selector and Results Info */}
                        <div className="p-3 border-bottom d-flex justify-content-between align-items-center flex-wrap gap-2">
                            <div className="d-flex align-items-center gap-2">
                                <span className="small">{t('show')}</span>
                                <Form.Select
                                    value={perPage}
                                    onChange={handlePerPageChange}
                                    style={{ width: '70px' }}
                                    size="sm"
                                >
                                    <option value={10}>10</option>
                                    <option value={25}>25</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                </Form.Select>
                                <span className="small">{t('entries')}</span>
                            </div>
                            <div className="small text-muted">
                                {t('showing')} {data.length > 0 ? ((currentPage - 1) * perPage) + 1 : 0} - {Math.min(currentPage * perPage, total)} {t('of')} {total} {t('vendors')}
                            </div>
                        </div>

                        <div className="table-responsive-wrapper">
                            <Table responsive hover className="table mb-0 vendor-table">
                                <thead>
                                    <tr>
                                        <th className="text-center" style={{ width: '5%' }}>#</th>
                                        <th style={{ width: '10%' }}>{t('image')}</th>
                                        <th style={{ width: '15%' }}>{t('name')}</th>
                                        <th style={{ width: '15%' }}>{t('company name')}</th>
                                        <th style={{ width: '10%' }}>{t('contact')}</th>
                                        <th style={{ width: '20%' }}>{t('address')}</th>
                                        <th className="text-center" style={{ width: '10%' }}>{t('actions')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.length > 0 ? (
                                        data.map((item, index) => (
                                            <tr key={item.id}>
                                                <td className="text-center">{((currentPage - 1) * perPage) + index + 1}</td>
                                                <td>
                                                    {item.img ? (
                                                        <img
                                                            src={`${process.env.REACT_APP_IMG_URL}/${item.img}`}
                                                            alt={item.name}
                                                            className="rounded"
                                                            style={{ width: '45px', height: '45px', objectFit: 'cover' }}
                                                        />
                                                    ) : (
                                                        <div className="bg-light rounded d-inline-flex align-items-center justify-content-center" style={{ width: '45px', height: '45px' }}>
                                                            <FiUser size={24} className="text-muted" />
                                                        </div>
                                                    )}
                                                </td>
                                                <td>
                                                    <strong className="text-primary">{item.name || '-'}</strong>
                                                </td>
                                                <td>{item.company_name || '-'}</td>
                                                <td>{item.contact || '-'}</td>
                                                <td>{item.address || '-'}</td>
                                                <td>
                                                    <div className="mc-table-action justify-content-center">
                                                        {hasPermission("vendor.edit") && (
                                                            <ButtonComponent
                                                                type="button"
                                                                className="material-icons edit"
                                                                onClick={() => openUpdateModal(item)}
                                                                title="Edit"
                                                            >
                                                                <FiEdit />
                                                            </ButtonComponent>
                                                        )}
                                                        <ButtonComponent
                                                            type="button"
                                                            className="material-icons view"
                                                            onClick={() => navigate(`/vendor-show/${item.id}`)}
                                                            title="View"
                                                        >
                                                            <FiEye />
                                                        </ButtonComponent>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="8" className="text-center py-5">
                                                <div className="text-muted">
                                                    <FiUser size={48} className="mb-3 opacity-25" />
                                                    <p>{t('No vendors found')}</p>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </div>

                        {/* Pagination Section */}
                        {lastPage > 1 && (
                            <div className="p-3 border-top d-flex justify-content-center">
                                <Pagination>
                                    {renderPagination()}
                                </Pagination>
                            </div>
                        )}
                    </div>
                </Col>
            </Row>

            {/* Store Modal */}
            <Modal show={storeModal} onHide={handleStoreModalClose} centered size="lg">
                <Modal.Header closeButton className="border-bottom p-2">
                    <Modal.Title>{t('add vendor')}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleStoreSubmit}>
                    <Modal.Body>
                        {storeErrors.general && (
                            <div className="alert alert-danger" role="alert">
                                {storeErrors.general}
                            </div>
                        )}
                        <Row className="g-3">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-bold small">{t('name')} *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="name"
                                        value={storeForm.name}
                                        onChange={handleStoreChange}
                                        isInvalid={!!storeErrors.name}
                                        disabled={storeLoading}
                                        required
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {storeErrors.name?.[0]}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-bold small">{t('company_name')}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="company_name"
                                        value={storeForm.company_name}
                                        onChange={handleStoreChange}
                                        isInvalid={!!storeErrors.company_name}
                                        disabled={storeLoading}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {storeErrors.company_name?.[0]}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-bold small">{t('Contact')}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="contact"
                                        value={storeForm.contact}
                                        onChange={handleStoreChange}
                                        isInvalid={!!storeErrors.contact}
                                        disabled={storeLoading}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {storeErrors.contact?.[0]}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            {/* <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-bold small">{t('email')}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="email"
                                        value={storeForm.email}
                                        onChange={handleStoreChange}
                                        isInvalid={!!storeErrors.email}
                                        disabled={storeLoading}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {storeErrors.email?.[0]}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col> */}
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-bold small">{t('image')}</Form.Label>
                                    <Form.Control
                                        type="file"
                                        name="img"
                                        accept="image/*"
                                        onChange={handleStoreFileChange}
                                        isInvalid={!!storeErrors.img}
                                        disabled={storeLoading}
                                        size="sm"
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {storeErrors.img?.[0]}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col md={12}>
                                <Form.Group>
                                    <Form.Label className="fw-bold small">{t('address')}</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        name="address"
                                        rows={3}
                                        value={storeForm.address}
                                        onChange={handleStoreChange}
                                        isInvalid={!!storeErrors.address}
                                        disabled={storeLoading}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {storeErrors.address?.[0]}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            {storeImagePreview && (
                                <Col md={12}>
                                    <Form.Group>
                                        <Form.Label className="fw-bold small">{t('image preview')}</Form.Label>
                                        <div>
                                            <img
                                                src={storeImagePreview}
                                                alt="Preview"
                                                className="rounded border"
                                                style={{ width: '80px', height: '80px', objectFit: 'cover' }}
                                            />
                                        </div>
                                    </Form.Group>
                                </Col>
                            )}
                        </Row>
                    </Modal.Body>
                    <Modal.Footer className="border-top p-2">
                        <ButtonComponent
                            type="button"
                            className="btn btn-secondary"
                            onClick={handleStoreModalClose}
                            disabled={storeLoading}
                        >
                            {t('cancel')}
                        </ButtonComponent>
                        <ButtonComponent
                            type="submit"
                            className="btn btn-primary"
                            disabled={storeLoading}
                        >
                            {storeLoading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2"></span>
                                    {t('saving...')}
                                </>
                            ) : (
                                t('save')
                            )}
                        </ButtonComponent>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Update Modal */}
            <Modal show={updateModal} onHide={handleUpdateModalClose} centered size="lg">
                <Modal.Header closeButton className="border-bottom p-2">
                    <Modal.Title>{t('edit vendor')}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleUpdateSubmit}>
                    <Modal.Body>
                        {updateErrors.general && (
                            <div className="alert alert-danger" role="alert">
                                {updateErrors.general}
                            </div>
                        )}
                        <Row className="g-3">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-bold small">{t('name')} *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="name"
                                        value={updateForm.name}
                                        onChange={handleUpdateChange}
                                        isInvalid={!!updateErrors.name}
                                        disabled={updateLoading}
                                        required
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {updateErrors.name?.[0]}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-bold small">{t('company_name')}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="company_name"
                                        value={updateForm.company_name}
                                        onChange={handleUpdateChange}
                                        isInvalid={!!updateErrors.company_name}
                                        disabled={updateLoading}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {updateErrors.company_name?.[0]}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-bold small">{t('Contact')}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="contact"
                                        value={updateForm.contact}
                                        onChange={handleUpdateChange}
                                        isInvalid={!!updateErrors.contact}
                                        disabled={updateLoading}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {updateErrors.contact?.[0]}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            {/* <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-bold small">{t('email')}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="email"
                                        value={updateForm.email}
                                        onChange={handleUpdateChange}
                                        isInvalid={!!updateErrors.email}
                                        disabled={updateLoading}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {updateErrors.email?.[0]}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col> */}
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-bold small">{t('image')}</Form.Label>
                                    <Form.Control
                                        type="file"
                                        name="img"
                                        accept="image/*"
                                        onChange={handleUpdateFileChange}
                                        isInvalid={!!updateErrors.img}
                                        disabled={updateLoading}
                                        size="sm"
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {updateErrors.img?.[0]}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col md={12}>
                                <Form.Group>
                                    <Form.Label className="fw-bold small">{t('address')}</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        name="address"
                                        rows={3}
                                        value={updateForm.address}
                                        onChange={handleUpdateChange}
                                        isInvalid={!!updateErrors.address}
                                        disabled={updateLoading}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {updateErrors.address?.[0]}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            {updateImagePreview && (
                                <Col md={12}>
                                    <Form.Group>
                                        <Form.Label className="fw-bold small">{t('current image')}</Form.Label>
                                        <div>
                                            <img
                                                src={updateImagePreview}
                                                alt="Current"
                                                className="rounded border"
                                                style={{ width: '80px', height: '80px', objectFit: 'cover' }}
                                            />
                                        </div>
                                    </Form.Group>
                                </Col>
                            )}
                        </Row>
                    </Modal.Body>
                    <Modal.Footer className="border-top p-2">
                        <ButtonComponent
                            type="button"
                            className="btn btn-secondary"
                            onClick={handleUpdateModalClose}
                            disabled={updateLoading}
                        >
                            {t('cancel')}
                        </ButtonComponent>
                        <ButtonComponent
                            type="submit"
                            className="btn btn-primary"
                            disabled={updateLoading}
                        >
                            {updateLoading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2"></span>
                                    {t('updating...')}
                                </>
                            ) : (
                                t('update')
                            )}
                        </ButtonComponent>
                    </Modal.Footer>
                </Form>
            </Modal>
        </PageLayout>
    );
}